import type { OperationalAttentionItem, OperationalTarget, ProjectHealthSummary } from '../../api';
import type { AttentionItem } from './projectHealth';

/**
 * operationalHealth turns the server's derivation (GET …/health) into what
 * the UI renders. It maps shapes and builds links; it never decides a level,
 * a severity or whether something needs attention — the server did, once,
 * for every surface (CO-95).
 */

export type SectionPath = (section: string, params?: Record<string, string>) => string;

/** targetPath is the link for a server-provided target. */
export function targetPath(target: OperationalTarget, sectionPath: SectionPath, environmentId?: string): string {
  if (target.section === 'server') return target.server_id ? `/app/servers/${target.server_id}` : '/app/servers';
  // An SSH environment's own page, where the SSH engine keeps its records (UX-08).
  if (target.section === 'environment' && environmentId) return `/app/environments/${environmentId}`;
  const base = sectionPath(target.section, target.params);
  return target.fragment ? `${base}#${target.fragment}` : base;
}

/** attentionFromHealth maps the server's attention items to list rows. */
export function attentionFromHealth(items: OperationalAttentionItem[] | undefined, sectionPath: SectionPath): AttentionItem[] {
  return (items ?? []).map((item) => ({
    key: item.id,
    severity: item.severity,
    category: item.category,
    kind: item.kind,
    title: item.title,
    detail: item.summary || undefined,
    action: item.action ? { label: item.action.label, to: targetPath(item.action.target, sectionPath, item.environment_id) } : undefined,
    when: item.since ? { label: item.since_label || 'Since', at: item.since } : undefined,
    related: (item.symptoms ?? []).map((symptom, index) => ({
      key: `${item.id}-${index}`,
      title: symptom.title,
      detail: symptom.detail || undefined,
    })),
    dismissible: item.dismissible,
    fingerprint: item.fingerprint,
  }));
}

/**
 * projectSectionPath builds the same links outside a project page: the
 * project's section, in the environment an item belongs to.
 */
export function projectSectionPath(projectId: string, environmentSlug?: string): SectionPath {
  return (section, params) => {
    const query = new URLSearchParams();
    if (environmentSlug) query.set('env', environmentSlug);
    for (const [key, value] of Object.entries(params ?? {})) query.set(key, value);
    const search = query.toString();
    return `/app/projects/${projectId}/${section}${search ? `?${search}` : ''}`;
  };
}

/** portfolioItemPath links a portfolio attention item to where it is fixed. */
export function portfolioItemPath(summary: ProjectHealthSummary, item: OperationalAttentionItem): string | undefined {
  if (!item.action) return undefined;
  const slug = summary.environments.find((env) => env.id === item.environment_id)?.slug;
  return targetPath(item.action.target, projectSectionPath(summary.project_id, slug), item.environment_id);
}

/** The environment an item belongs to, by name. */
export function itemEnvironmentName(summary: ProjectHealthSummary, item: OperationalAttentionItem): string | undefined {
  return summary.environments.find((env) => env.id === item.environment_id)?.name;
}

/** naturalCompare puts "Warehouse EU #2" before "Warehouse EU #17". */
export function naturalCompare(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

/** The words for a server's derived agent state (domain.AgentState). */
export const agentStateLabel: Record<string, string> = {
  connected: 'Connected',
  stale: 'No contact',
  offline: 'Offline',
  waiting: 'Waiting for the agent',
  ssh: 'Reached over SSH',
  none: 'No server',
};
